<script setup lang="ts">
import { computed } from 'vue';
import { useMenuImageUrl } from '@shared/app/composables/useMenuImageUrl';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';

const { createMenuMainImageUrl } = useMenuImageUrl();
const { createRecipeMainImageUrl } = useRecipeImageUrl();

type RecipeItem = {
  id: number;
  title: string;
  main_image?: string | null;
};

const props = withDefaults(
  defineProps<{
    oldIdentifier: string;
    menuId: number;
    title: string;
    mainImage?: string | null;
    energy: number;
    recipeCount: number;
    tags?: string[];
    recipes?: RecipeItem[];
    rank?: number;
  }>(),
  {
    mainImage: undefined,
    tags: () => [],
    recipes: () => [],
    rank: undefined,
  },
);

// 献立にmain_imageが設定されている場合はそれを利用し、そうでなければ1番目のレシピ画像を利用する
const menuImageUrl = computed(() => {
  if (props.mainImage) {
    return createMenuMainImageUrl({
      menuId: props.menuId,
      filename: props.mainImage,
      w: 640,
      h: 480,
    });
  }

  const firstRecipe = props.recipes[0];

  if (firstRecipe?.main_image) {
    return createRecipeMainImageUrl({
      recipeId: firstRecipe.id,
      filename: firstRecipe.main_image,
      w: 640,
      h: 480,
    });
  }

  return undefined;
});

// NOTE: ４つのアイテムが入った配列で返却
// レイアウトの表示崩れを防ぐため、足りない分はnullで返し、空divを描画する
// 最初のレシピをスキップして2番目から5番目までのレシピを表示する(1枚目とレシピ画像が同じ場合があるため)
const recipeItems = computed(() => {
  const recipes = props.recipes.slice(1).slice(0, 4);

  return Array.from({ length: 4 }, (_, i) => {
    const recipe = recipes[i];

    return {
      image: recipe?.main_image
        ? createRecipeMainImageUrl({
            recipeId: recipe.id,
            filename: recipe.main_image,
            w: 100,
            h: 100,
          })
        : null,
      title: recipe?.title,
    };
  });
});

const menuLink = computed(() => {
  return `/public/article/menu/${props.oldIdentifier}`;
});
</script>

<template>
  <NuxtLink :to="menuLink">
    <div class="relative">
      <AtomsRankBadge v-if="rank && rank <= 3" :rank="rank" />
      <div data-testid="menu-image">
        <img v-if="menuImageUrl" :src="menuImageUrl" class="rounded-t" />
        <AtomsPlaceholderImage
          v-else
          class="w-full object-cover aspect-[250/200] rounded-t bg-white"
        />
      </div>

      <div class="flex w-full absolute -bottom-5 px-1">
        <div
          v-for="(recipeImage, i) in recipeItems"
          :key="i"
          class="w-full mx-1"
          data-testid="recipe"
        >
          <img
            v-if="recipeImage.image"
            :src="recipeImage.image"
            :alt="recipeImage.title"
            class="w-full h-full object-cover rounded border border-white"
          />
        </div>
      </div>
    </div>

    <div
      class="bg-white border border-t-0 border-b-2 border-cream-400 rounded-b px-2 py-7"
    >
      <div class="mb-1 flex items-baseline">
        <p class="mr-2 flex items-baseline">
          <img
            src="~/assets/img/fire.png"
            class="mr-0.5 relative top-[2px] w-[16px]"
          />
          <span class="font-bold text-pink-400">{{ energy }}</span>
          <span>kcal</span>
        </p>

        <span class="text-brown-300">
          <span class="font-dietplus-runes before:content-['\ea2b'] mr-1" />
          <b>{{ recipeCount }}</b
          >品の献立</span
        >
      </div>

      <h3 class="font-bold text-gray-500 line-clamp-2 h-[3em]">
        {{ title }}
      </h3>

      <p class="text-brown-300 line-clamp-1 text-sm h-[1.5em]">
        <span v-if="tags.length > 0">{{ tags.join('、') }}</span>
      </p>
    </div>
  </NuxtLink>
</template>
